import type { ListBuilderConfiguration, ListBuilderList } from '../../types';
import { LICENSE_STATUS_STYLE_MAP } from '../license/status-style-map';

/**
 * Registry list configuration (Compliance → Registry).
 *
 * Surfaces records from `workd_mission_control.lm_registry` with a
 * match-status overlay against the current tenant's `client_license_match`
 * table. The data shape is project-flat (no joined entity) — the
 * `objectTable` value of `'lm_registry'` is just the registry-side name we
 * use to scope filter configs (`registryKeywords`, `registrySourceType`,
 * `registryCategory`, etc.); it
 * does NOT correspond to a TypeORM entity and is never resolved through
 * `applyFiltersToQueryBuilder`. The backend's `RegistryService.listRegistry`
 * reads the same filter keys directly off `RegistryQueryDto.filters` and
 * builds raw SQL.
 *
 * PD-14103/PD-14104: the registry page is a verification-first lookup —
 * the page's search form drives this list through LOCKED filterRules
 * (state + source + 3+-char term, mirrored server-side), the endpoint
 * returns the top 50 matches only, and there is no pagination: the
 * server pins totalPages to 1 (so the pager never renders) and `paging`
 * below pins pageSize to the same 50-row cap so the footer count line
 * matches what's actually on screen. The filter configs remain registered
 * for the query plumbing even though the FilterGroup chrome is hidden on
 * the page.
 */

const allRegistryList: ListBuilderList = {
	id: 'default-registry-all',
	cacheTTL: 600000,
	name: 'All Registry Records',
	type: 'default',
	active: true,
	sortOrder: 1,
	filterRules: {},
	columns: [
		{
			id: 'registry-business-name',
			// Business name on top in default text, registry address below
			// as a single line in muted secondary. Mirrors the layout of
			// the Match column (client name + client_address) so the two
			// addresses sit at consistent visual weights.
			fields: [
				{
					id: 'business-name-field',
					// `displayName` is a server-coalesced
					// `business_name → entity_name → dba_name → first/last_name
					// → '(no name on record)'` projection that skips
					// empty / whitespace / placeholder strings (`?`, `-`).
					// Sorting on this column uses the same COALESCE
					// expression server-side, so the row order the user
					// sees lines up exactly with the values rendered.
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'displayName',
							objectTable: 'lm_registry',
						},
						defaultValue: '(no name on record)',
					},
					styleConfig: {
						size: 'md',
						weight: 'medium',
						colorRule: { type: 'default' },
					},
					displayStyle: 'text',
				},
				{
					id: 'business-name-line-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'registry-address-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'addressFull',
							objectTable: 'lm_registry',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
					},
					displayStyle: 'text',
				},
			],
			label: 'Business',
			headers: [{ id: 'h-business', text: 'Business' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'entityName',
					objectTable: 'lm_registry',
				},
			],
			minWidth: 280,
		},
		{
			id: 'registry-match',
			// Three stacked fields: client-name link (blue, matches the
			// styling of license_number on the Licenses list), then the
			// address bound to the matched client_license below in
			// secondary text. When unmatched, `matchedClientId` is null so
			// the link resolver no-ops and the "Unmatched" placeholder
			// renders as plain text; `matchedClientAddress` is null so
			// the second line renders as `-` (handled by the framework's
			// empty-text fallback).
			fields: [
				{
					id: 'matched-client-name',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'matchedClientName',
							objectTable: 'lm_registry',
						},
						defaultValue: 'Unmatched',
					},
					link: {
						enabled: true,
						type: 'object',
						objectTable: 'client',
						idField: 'matchedClientId',
					},
					styleConfig: {
						size: 'sm',
						weight: 'medium',
						colorRule: { type: 'default' },
					},
					displayStyle: 'text',
				},
				{
					id: 'matched-client-line-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'matched-client-address',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'matchedClientAddress',
							objectTable: 'lm_registry',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
					},
					displayStyle: 'text',
				},
			],
			label: 'Match',
			headers: [{ id: 'h-match', text: 'Match' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			minWidth: 220,
		},
		{
			id: 'registry-license-type',
			// License # on top (default white) with type below in muted
			// secondary. Sortable on license number — the type's sort
			// rarely makes sense on its own.
			fields: [
				{
					id: 'license-number-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'licenseNumber',
							objectTable: 'lm_registry',
						},
					},
					styleConfig: {
						size: 'sm',
						weight: 'medium',
						colorRule: { type: 'default' },
					},
					displayStyle: 'text',
				},
				{
					id: 'license-type-line-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'license-type-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'licenseType',
							objectTable: 'lm_registry',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
					},
					displayStyle: 'text',
				},
			],
			label: 'License # / Type',
			headers: [{ id: 'h-license-type', text: 'License # / Type' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'licenseNumber',
					objectTable: 'lm_registry',
				},
			],
			width: 200,
		},
		{
			id: 'registry-status',
			fields: [
				{
					id: 'status-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'licenseStatus',
							objectTable: 'lm_registry',
						},
						defaultValue: 'Unknown',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'sm',
						colorRule: {
							type: 'status_map',
							statusMap: { ...LICENSE_STATUS_STYLE_MAP },
						},
					},
				},
			],
			label: 'Status',
			headers: [{ id: 'h-status', text: 'Status' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'licenseStatus',
					objectTable: 'lm_registry',
				},
			],
			width: 110,
		},
		{
			id: 'registry-expiration',
			// Two stacked dates: Issued on top in default text color,
			// Expires on the bottom in muted secondary. Matches the
			// "primary value on line 1, secondary on line 2" pattern of
			// every other multi-line column on this list.
			fields: [
				{
					id: 'issue-date-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'issueDate',
							objectTable: 'lm_registry',
						},
					},
					styleConfig: { size: 'sm', colorRule: { type: 'default' } },
					displayStyle: 'date',
				},
				{
					id: 'expiration-line-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'expiration-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'expirationDate',
							objectTable: 'lm_registry',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
					},
					displayStyle: 'date',
				},
			],
			label: 'Issued / Expires',
			headers: [{ id: 'h-expiration', text: 'Issued / Expires' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'expirationDate',
					objectTable: 'lm_registry',
				},
			],
			width: 132,
		},
		{
			id: 'registry-actions',
			fields: [
				{
					id: 'actions-dropdown',
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					displayStyle: 'actions_dropdown',
					dropdownActions: [
						{
							id: 'create-lead-action',
							label: 'Create Lead',
							iconName: 'plus',
							callbackName: 'createLead',
							variant: 'primary',
							// F43: hide the inline Create Lead action when
							// either (a) the row already has a confirmed
							// `client_license_match` for this tenant, or
							// (b) the row has at least one live
							// `pending_review` match awaiting human review
							// in the Pending Match tab. In either case the
							// rep should resolve the existing match
							// (confirm-or-reject) rather than start a
							// parallel lead, and the callback's toast +
							// backend 409 are last-resort safety nets we
							// no longer want as the primary deterrent.
							//
							// `canCreateLead` is a single derived boolean
							// the registry service projects per row
							// (matchedClientId == null && pendingMatchCount
							// === 0) so the visibility rule stays single-
							// clause — the engine's DropdownActionVisibility
							// doesn't support compound predicates and adding
							// a JS callback for one boolean would be heavier
							// than the field.
							visibility: 'visible',
							conditionalVisibility: {
								field: 'canCreateLead',
								condition: 'equals',
								value: true,
							},
							requiredPermission: 'clients:leads:create',
						},
					],
				},
			],
			label: '',
			headers: [{ id: 'h-actions', text: '' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			width: 64,
		},
	],
	defaultSortId: 'column-registry-business-name-asc',
	actions: [],
};

export const REGISTRY_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [allRegistryList],
	// Pinned to the server's REGISTRY_RESULT_CAP (PD-14104): the endpoint
	// always returns at most 50 rows with totalPages=1, so offering other
	// page sizes would only desync the footer count from the table.
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [50],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'registryKeywords',
			'registrySourceType',
			'registryCategory',
			'registryState',
			'registryStatus',
			'registryMatchStatus',
		],
	},
	// Relevance sort opts in via `supportsRelevance: true` on the
	// `registryKeywords` filter — `useListData.ts:786-803` auto-injects the
	// dropdown option whenever a relevance-capable filter is active, so an
	// explicit `tableSorts: [{ id: 'relevance', ... }]` entry here would be
	// redundant. The backend wiring in `registry.service.ts::_resolveOrderBy`
	// also defends with a fallback to the entityName-coalesced default if
	// `sortField='relevance'` ever arrives without an active search.
	objects: [
		{
			baseUrl: '/registry/:id',
			objectLabel: 'Registry Record',
			objectTable: 'lm_registry',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'businessName', fieldLabel: 'Business Name' },
				{ fieldName: 'entityName', fieldLabel: 'Entity Name' },
				{ fieldName: 'dbaName', fieldLabel: 'DBA' },
				{ fieldName: 'licenseNumber', fieldLabel: 'License #' },
				{ fieldName: 'licenseType', fieldLabel: 'Type' },
				{ fieldName: 'stateCode', fieldLabel: 'State' },
				{ fieldName: 'licenseStatus', fieldLabel: 'Status' },
				{ fieldName: 'expirationDate', fieldLabel: 'Expires' },
				{ fieldName: 'city', fieldLabel: 'City' },
				{ fieldName: 'npiNumber', fieldLabel: 'NPI' },
				{ fieldName: 'matchedClientName', fieldLabel: 'Matched Client' },
			],
		},
		// Required so the Match column's `link: { type: 'object',
		// objectTable: 'client' }` resolves to `/clients/:id`. Without
		// this entry the link helper falls back to `/client/<id>` (no 's')
		// which 404s on the SPA router.
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [],
		},
	],
};
